/**
 * ResearchSourcesPanel — shows the data-provenance breakdown for a
 * company or investor. Renders the response from
 *   GET /companies/{id}/research/sources
 *   GET /investors/{id}/research/sources
 *
 * Use as a drop-in panel on the detail pages: the operator and the
 * (eventually) entity owner get to see exactly which external sources
 * we hit and what we found.
 */
import React, { useEffect, useState } from 'react';
import {
  Database, Loader2, ExternalLink, FileSearch,
  Sparkles, Globe, Building2, Brain,
} from 'lucide-react';
import api from '../services/api';

interface SourceRow {
  source_type: string;
  source_label: string;
  category: string;
  status: string;
  retrieved_at: string | null;
  items_found: number | null;
  summary: string;
  urls: string[];
}

interface Props {
  entityKind: 'company' | 'investor';
  entityId: number;
}

const CATEGORY_ICON: Record<string, React.ReactNode> = {
  regulatory: <FileSearch className="w-4 h-4 text-amber-600" />,
  media: <Globe className="w-4 h-4 text-sky-600" />,
  ai: <Sparkles className="w-4 h-4 text-indigo-500" />,
  third_party: <Building2 className="w-4 h-4 text-emerald-600" />,
  internal: <Brain className="w-4 h-4 text-slate-600" />,
  other: <Database className="w-4 h-4 text-gray-500" />,
};

const CATEGORY_LABEL: Record<string, string> = {
  regulatory: 'Regulatory & filings',
  media: 'Media & transcripts',
  ai: 'AI analysis',
  third_party: 'Third-party APIs',
  internal: 'Internal models',
  other: 'Other',
  paid_available: 'Paid options — additional intelligence available at cost',
};

const ResearchSourcesPanel: React.FC<Props> = ({ entityKind, entityId }) => {
  const [rows, setRows] = useState<SourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const url = `/${entityKind === 'company' ? 'companies' : 'investors'}/${entityId}/research/sources`;
        const { data } = await api.get<SourceRow[]>(url);
        if (!cancelled) setRows(data);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Failed to load sources');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [entityKind, entityId]);

  // Group rows by category for visual organization.
  const grouped: Record<string, SourceRow[]> = {};
  rows.forEach((r) => {
    (grouped[r.category] ||= []).push(r);
  });
  const orderedCategories = [
    'regulatory', 'media', 'ai', 'third_party', 'internal', 'other', 'paid_available',
  ].filter((c) => grouped[c]?.length);

  // Within each category, push "no_data" rows to the bottom so the
  // operator sees what fired before what's missing.
  Object.values(grouped).forEach((arr) => {
    arr.sort((a, b) => {
      const aFired = a.status !== 'no_data' && a.status !== 'available_paid' ? 0 : 1;
      const bFired = b.status !== 'no_data' && b.status !== 'available_paid' ? 0 : 1;
      return aFired - bFired;
    });
  });

  return (
    <section className="bg-white border border-gray-200 rounded-lg p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
          <Database className="w-4 h-4 text-gray-400" />
          Sources used to assess this {entityKind}
        </h2>
        {!loading && (
          <span className="text-xs text-gray-500">
            {rows.length} source{rows.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-500 py-4">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div className="text-sm text-red-600 py-2">{error}</div>
      ) : rows.length === 0 ? (
        <div className="text-sm text-gray-500 py-3">
          No research has been performed on this {entityKind} yet.
        </div>
      ) : (
        <div className="space-y-4">
          {orderedCategories.map((cat) => (
            <div key={cat}>
              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
                {CATEGORY_LABEL[cat]}
              </div>
              <div className="space-y-2">
                {grouped[cat].map((r) => {
                  const isNoData = r.status === 'no_data';
                  const isPaid = r.status === 'available_paid';
                  const rowClass = isPaid
                    ? 'flex items-start gap-3 p-2.5 border border-amber-200 bg-amber-50/40 rounded'
                    : isNoData
                      ? 'flex items-start gap-3 p-2.5 border border-gray-100 bg-gray-50/40 rounded opacity-75'
                      : 'flex items-start gap-3 p-2.5 border border-gray-100 rounded';
                  return (
                  <div key={r.source_type} className={rowClass}>
                    <div className="flex-shrink-0 mt-0.5">
                      {CATEGORY_ICON[r.category] || CATEGORY_ICON.other}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`font-medium text-sm ${isNoData || isPaid ? 'text-gray-700' : ''}`}>
                          {r.source_label}
                        </span>
                        {r.items_found !== null && r.items_found > 0 && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-50 text-indigo-700">
                            {r.items_found} item{r.items_found === 1 ? '' : 's'}
                          </span>
                        )}
                        {isPaid && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-100 text-amber-800">
                            paid · upgrade
                          </span>
                        )}
                        {isNoData && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-gray-200 text-gray-700">
                            no data
                          </span>
                        )}
                        {!isNoData && !isPaid && r.status !== 'success' && (
                          <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${
                            r.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {r.status}
                          </span>
                        )}
                      </div>
                      {r.summary && (
                        <div className="text-xs text-gray-600 mt-0.5">{r.summary}</div>
                      )}
                      {r.retrieved_at && (
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {new Date(r.retrieved_at).toLocaleString()}
                        </div>
                      )}
                      {r.urls.length > 0 && (
                        <div className="mt-1 space-y-0.5">
                          {r.urls.slice(0, 3).map((url, i) => (
                            <a
                              key={i}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline truncate max-w-full"
                            >
                              <ExternalLink className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{url}</span>
                            </a>
                          ))}
                          {r.urls.length > 3 && (
                            <div className="text-[10px] text-gray-500">
                              +{r.urls.length - 3} more URL{r.urls.length - 3 === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default ResearchSourcesPanel;
